// client/src/pages/actions/AddAction.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/api";

const TYPES = ["events", "games", "tracking", "access"];

const pageLayoutStyle = {
  display: 'flex',
  gap: '20px',
  alignItems: 'flex-start'
};

const leftColumnStyle = {
  flex: 1,
  minWidth: 0
};

const rightColumnStyle = {
  width: '320px',
  flexShrink: 0
};

const cardPanelStyle = {
  background: "#eef2f7",
  padding: "16px 20px"
};

const whiteInnerCardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16
};

export default function AddAction() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── core fields
  const [name, setName] = useState("");
  const [type, setType] = useState(TYPES[0]);
  const [status, setStatus] = useState("active");

  // ── relations
  const [datasetId, setDataSetId] = useState("");
  const [datasetName, setDataSetName] = useState("");
  const [landingPageId, setLandingPageId] = useState("");
  const [landscapeCardDesignId, setLandscapeCardDesignId] = useState("");
  const [portraitCardDesignId, setPortraitCardDesignId] = useState("");
  const [printMode, setPrintMode] = useState("gallery");

  // ── lists
  const [landingPages, setLandingPages] = useState([]);
  const [cards, setCards] = useState([]);
  const [availablePrinters, setAvailablePrinters] = useState([]);

  // ── printers (multi)
  const [selectedPrinters, setSelectedPrinters] = useState([]);
  const [printerGroups, setPrinterGroups] = useState([]);
  const [printerGroupId, setPrinterGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // shared styles (labels + inputs on the same row)
  const rowStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: "220px minmax(0,1fr)",
      alignItems: "center",
      gap: 12,
    }),
    []
  );
  const controlStyle = useMemo(() => ({ width: "100%", boxSizing: "border-box" }), []);


  async function fetchPrinterGroups() {
    const res = await apiFetch('/api/printmanager/printergroups', { method: 'GET' });
    return Array.isArray(res) ? res : res?.items || [];
  }

  // dataset id from query/state
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const fromQS = qs.get("datasetId") || qs.get("datasetId");
    const fromState = location.state?.datasetId || location.state?.datasetId;
    const dsId = fromQS || fromState || "";
    setDataSetId(dsId);

    (async () => {
      try {
        if (!dsId) return;
        const ds = await apiFetch(`/datasets/get/${encodeURIComponent(dsId)}`, { method: "GET" });
        setDataSetName(ds?.name || dsId);
      } catch {
        setDataSetName(dsId || "");
      }
    })();
  }, [location.search, location.state]);

  // helper to fetch lists with fallbacks
  const fetchListWithFallback = async (attempts) => {
    for (const att of attempts) {
      try {
        const res = await apiFetch(att.path, { method: att.method || "GET", body: att.body });
        const items = Array.isArray(res) ? res : res?.items || [];
        if (items.length || att.allowEmpty) return items;
      } catch {}
    }
    return [];
  };

  // load LPs, Cards, Printers once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        const lpItems = await fetchListWithFallback([
          { path: "/landingpages/list", method: "POST", body: {} },
          { path: "/landingpages/getFiltered", method: "POST", body: { cnt: 0, lmt: 1000 } },
          { path: "/landingpages/getAll", method: "POST", body: {} }
        ]);
        if (!mounted) return;
        setLandingPages(lpItems);

        const cardItems = await fetchListWithFallback([
          { path: "/cards/getAll", method: "GET" },
          { path: "/cards/list", method: "POST", body: {} },
          { path: "/cards/getFiltered", method: "POST", body: { cnt: 0, lmt: 1000 } }
        ]);
        if (!mounted) return;
        setCards(cardItems);

        const groups = await fetchPrinterGroups();
        if (mounted) setPrinterGroups(groups);

        try {
          const snap = await apiFetch("/snapshot", { method: "GET" });
          let printers = [];
          if (Array.isArray(snap)) printers = snap;
          else if (Array.isArray(snap?.printers)) printers = snap.printers;
          printers = printers
            .map((p) => (typeof p === "string" ? { name: p } : p))
            .filter((p) => p?.name);
          if (!mounted) return;
          setAvailablePrinters(printers);
        } catch {
          if (!mounted) return;
          setAvailablePrinters([]);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load options");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const togglePrinter = (pname) => {
    setSelectedPrinters((prev) =>
      prev.includes(pname) ? prev.filter((n) => n !== pname) : [...prev, pname]
    );
  };

  const handleReturn = () => {
    if (datasetId) {
      navigate(`/datasets/actions/${encodeURIComponent(datasetId)}`);
    } else {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    try {
      if (!datasetId) {
        toast.error("Missing Data Set context.");
        return;
      }
      if (!name.trim()) {
        toast.error("Please enter an Action Name.");
        return;
      }
      setSaving(true);
      const body = {
        name,
        type,
        status,
        datasetId,
        landingPageId,
        landscapeCardDesignId,
        portraitCardDesignId,
        printMode,
        printerGroupId: printerGroupId || null,
        printerNames: selectedPrinters,
        printers: selectedPrinters,
      };
      const res = await apiFetch("/actions/create", { method: "POST", body });
      const newId = res?._id || res?.id;
      toast.success("Action created.");
      if (newId) {
        // jump to edit so user sees QR/link immediately
        navigate(`/actions/edit/${encodeURIComponent(newId)}?datasetId=${encodeURIComponent(datasetId)}`);
      } else {
        handleReturn();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-card" style={cardPanelStyle}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{marginLeft:"20px"}}>
      {/* Title left, actions right (match Edit Data Set style) */}
      <div
        className="datatable-header-row"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>Add Action</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-return" onClick={handleReturn}>Return</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Action"}
          </button>
        </div>
      </div>

      {/* Main card */}
      <div style={pageLayoutStyle}>
        {/* LEFT — form */}
        <div style={leftColumnStyle}>
          <div className="detail-card" style={cardPanelStyle}>
            <div style={rowStyle}>
              <label>Data Set</label>
              <input
                value={datasetName || datasetId || "(unknown)"}
                readOnly
                style={{ ...controlStyle, background: "#fff", border:'none' }}
              /><br/>
            </div>
            <div style={rowStyle}>
              <label>Action Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} style={controlStyle} />
            </div>

            <div style={rowStyle}>
              <label>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} style={controlStyle}>
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Landing Page</label>
              <select
                value={landingPageId}
                onChange={(e) => setLandingPageId(e.target.value)}
                style={controlStyle}
              >
                <option value="">-- Select --</option>
                {landingPages.map((lp) => (
                  <option key={lp._id} value={lp._id}>
                    {lp.name || lp.title || lp._id}
                  </option>
                ))}
              </select>
            </div>
            <div style={rowStyle}>
              <label>Landscape Card</label>

              <select
                value={landscapeCardDesignId}
                onChange={e => setLandscapeCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Portrait Card</label>

              <select
                value={portraitCardDesignId}
                onChange={e => setPortraitCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={controlStyle}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>


            <div style={rowStyle}>
              <label>Printer Group</label>
              <select
                value={printerGroupId}
                onChange={(e) => setPrinterGroupId(e.target.value)}
                style={controlStyle}
              >
                <option value="">-- Select Printer Group --</option>
                {printerGroups.map(g => (
                  <option key={g._id} value={g._id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <hr style={{ margin: "20px 0" }} />

            {/* Printers — checkbox grid */}
            <div style={rowStyle}>
              <label>Printers</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 8,
                }}
              >
                {availablePrinters.length === 0 ? (
                  <div style={{ color: "#666" }}>No printers available</div>
                ) : (
                  availablePrinters.map((p) => {
                    const pname = p.name || "";
                    return (
                      <label
                        key={pname}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 10px",
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPrinters.includes(pname)}
                          onChange={() => togglePrinter(pname)}
                        />
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {pname}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
            <div style={rowStyle}>
              <label>Print Mode</label>

              <select
                value={printMode}
                onChange={(e) =>
                  setPrintMode(e.target.value)
                }
                style={{ width: "100%" }}
              >
                <option value="gallery">
                  Gallery (manual print)
                </option>

                <option value="immediate">
                  Immediate Print
                </option>

                <option value="none">
                  No Print
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* RIGHT — info panel */}
        <div style={rightColumnStyle}>

          <div className="detail-card" style={cardPanelStyle}>

            <h3 style={{ marginTop: 0 }}>
              Action URL
            </h3>
            <div style={whiteInnerCardStyle}>
              <div
                style={{
                  wordBreak: 'break-all',
                  fontSize: '.85rem',
                  marginBottom: '20px',
                  color: '#666'
                }}
              >
                Save the Action to generate
                the URL and QR Code.
              </div>

              <div
                style={{
                  textAlign: 'center',
                  padding: '30px 0',
                  color: '#999'
                }}
              >
                QR Code Available After Save
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
